'use client';
import { isFunction, omitUndefined, objectEntries, isString } from '@s7/utils';
import { memoizedEmitter } from './memoizedEmitter.js';
import { useCSS } from './core/css/useCSS.js';
import { mergeStyles } from '../mergeStyles/mergeStyles.js';
import { useTheme, useEmotionCache } from '../../theming/propagation/ThemeProvider.js';

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
const dummy = {};
const wrapUnsafe = (some)=>some != null ? some : dummy;
const createStyles = (styles)=>{
    const emit = isFunction(styles) ? memoizedEmitter(styles) : ()=>styles;
    return (params, options, __ident)=>{
        const { classNames, styles, size, variant, __withStaticSelectors = true } = options || {};
        const cleanParams = params ? omitUndefined(params) : null;
        const variations = {
            size,
            variant
        };
        const theme = useTheme();
        const cache = useEmotionCache();
        const { css, cx } = useCSS();
        /*
         * Накапливает переопределяемые стили;
         *
         * Выполнение поверхностного копирования:
         *
         * Необходимость:
         * Требуется, чтобы корректно применить дальнейшие переопределения
         * (то есть только для одного прецедента использования).
         *
         * Достаточность:
         * В силу того, что mergeStyles выполняет поверхностное копирование объектов на уровне вложенности ниже,
         * поверхностного копирования в этом месте достаточно.
         * */ const cssObjects = _extends({}, emit(theme, wrapUnsafe(cleanParams), wrapUnsafe(variations)));
        const inject = (styles)=>mergeStyles(cssObjects, isFunction(styles) ? styles(theme, wrapUnsafe(cleanParams), wrapUnsafe(variations)) : styles);
        const classNamesOverride = {};
        /*
         * Порядок, в котором вызываются assignAdjacentStyles, определяет приоритет
         * одних стилей над другими.
         * */ if (__ident && theme.components[__ident]) {
            const { styles, variants, sizes, classNames } = theme.components[__ident];
            /*
             * Добавляет стили, вычисляемые на основе `variants` из темы;
             * */ if (variants && variant && variant in variants) {
                inject(variants[variant]);
            }
            /*
             * Добавляет стили, вычисляемые на основе `sizes` из темы;
             * */ if (sizes && size && size in sizes) {
                inject(sizes[size]);
            }
            /*
             * Добавляет стили, вычисляемые на основе `styles` из темы;
             * */ if (styles) {
                inject(styles);
            }
            if (classNames) {
                for (const [key, value] of objectEntries(classNames)){
                    if (!isString(classNamesOverride[key])) {
                        classNamesOverride[key] = value;
                    } else {
                        classNamesOverride[key] += ' ' + value;
                    }
                }
            }
        }
        if (styles) {
            /*
             * Добавляет стили, вычисляемые на основе `styles`, определённых на уровне компонента;
             * */ inject(styles);
        }
        const classes = {};
        for(const key in cssObjects){
            classes[key] = cx(/*
                 * Отображаем CSSObject в имена классов, добавляя стили в кэш с помощью css.
                 * Генерация основных стилей, кэшированием занимается emotion.
                 * */ css(cssObjects[key]), /*
                 * Дополнительные имена классов
                 * */ classNamesOverride[key], classNames == null ? void 0 : classNames[key], __ident && __withStaticSelectors && `${cache.key}-${__ident}-${key}`);
        }
        return [
            classes,
            cx,
            theme
        ];
    };
};

export { createStyles };
//# sourceMappingURL=createStyles.js.map
